/* ============================================================================
   Carga del dataset y accesos con nombre.

   El archivo lo genera `npm run ingest` y se versiona en el repositorio: la app
   es estática y no consulta nada en tiempo de ejecución. Eso la hace barata de
   hospedar y, más importante, reproducible: lo que se ve en pantalla es
   exactamente lo que se ingirió y validó.
   ========================================================================== */

import raw from '../../data/build/canasta.json'
import type { Dataset, Series } from './schema'

export const dataset = raw as unknown as Dataset

/** Identificadores de serie que la app conoce por nombre. */
export const SERIES = {
  canastaAlimentariaRural: 'canasta-alimentaria-rural',
  canastaAlimentariaUrbana: 'canasta-alimentaria-urbana',
  canastaAmpliadaRural: 'canasta-ampliada-rural',
  canastaAmpliadaUrbana: 'canasta-ampliada-urbana',
  salarioMinimoDiario: 'salario-minimo-diario',
} as const

export type Ambito = 'urbano' | 'rural'

export const AMBITO_OPTIONS: readonly { value: Ambito; label: string; hint: string }[] = [
  { value: 'urbano', label: 'Urbano', hint: 'Localidades de 2 500 habitantes o más' },
  { value: 'rural', label: 'Rural', hint: 'Localidades de menos de 2 500 habitantes' },
]

/** Forma masculina: «ámbito urbano». */
export const AMBITO_LABEL: Record<Ambito, string> = {
  urbano: 'urbano',
  rural: 'rural',
}

/** Forma femenina: «canasta alimentaria urbana». El género tiene que concordar. */
export const AMBITO_LABEL_F: Record<Ambito, string> = {
  urbano: 'urbana',
  rural: 'rural',
}

/** Color de marca asignado por ámbito. Fijo: el color sigue a la entidad. */
export const AMBITO_COLOR: Record<Ambito, string> = {
  urbano: 'var(--series-1)',
  rural: 'var(--series-2)',
}

export function canastaAlimentaria(ambito: Ambito): string {
  return ambito === 'urbano' ? SERIES.canastaAlimentariaUrbana : SERIES.canastaAlimentariaRural
}

export function canastaAmpliada(ambito: Ambito): string {
  return ambito === 'urbano' ? SERIES.canastaAmpliadaUrbana : SERIES.canastaAmpliadaRural
}

const byId = new Map(dataset.series.map((s) => [s.id, s]))

export function series(id: string): Series {
  const found = byId.get(id)
  if (!found) {
    throw new Error(
      `El dataset no tiene la serie «${id}». Series disponibles: ${[...byId.keys()].join(', ')}`,
    )
  }
  return found
}

export function maybeSeries(id: string): Series | null {
  return byId.get(id) ?? null
}

export function sourceName(id: string): string {
  const source = dataset.sources.find((s) => s.id === id)
  return source ? `${source.institution} · ${source.name}` : id
}

export function sourceShort(id: string): string {
  const source = dataset.sources.find((s) => s.id === id)
  return source?.institution ?? id
}

export function notesFor(target: string) {
  return dataset.notes.filter((note) => (note.appliesTo ?? []).includes(target))
}

/** Tamaño promedio del hogar en México. Se usa como valor inicial, es editable. */
export const DEFAULT_HOUSEHOLD_SIZE = 4
